/**
 * Root React aplikacija: provideri (Query, Auth, PageTitle), rute i globalni layout.
 */

import React, { Suspense, lazy } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { queryClient } from '@/core/api/queryClient';
import '@/shared/styles/common.css';
import './App.css';
import { AuthProvider, useAuth } from '@/shared/context/AuthContext';
import { APP_PATHS } from '@/shared/constants/routes';
import { TOAST_DURATION, GA_TRACKING_ID } from '@/shared/constants';
import SigninRedirectPage from './features/auth/SigninRedirectPage';
import SignoutCallbackPage from './features/auth/SignoutCallbackPage';
import LoginLaunchPage from './features/auth/LoginLaunchPage';
import HomeAppHeader from '@/features/public/home/components/HomeAppHeader';
import Footer from './shared/components/layout/Footer';
import LoadingOverlay from './shared/components/LoadingOverlay';
import LoadingSpinner from './shared/components/LoadingSpinner';
import ScrollToTop from './shared/components/ScrollToTop';
import { GoogleAnalyticsInit, GoogleAnalyticsPageViews } from './shared/components/analytics/GoogleAnalytics';
import RouteTransitionOverlay from './shared/components/RouteTransitionOverlay';
import AccessibilityPanelWrapper from '@/accessibility/AccessibilityPanelWrapper';
import { useRouteTitle } from './shared/hooks/useDocumentTitle';
import { PageTitleProvider } from '@/shared/context/PageTitleContext';
import { HomeSectionNavProvider } from '@/features/public/home/context/HomeSectionNavContext';
import ProtectedRoute from './shared/components/ProtectedRoute';
import { useJustLoggedInFeedback } from '@/shared/hooks/useJustLoggedInFeedback';
import { usePrefetchUserProfileOnIntent } from '@/features/user/profile/hooks/usePrefetchUserProfileOnIntent';
import NotFoundPage from './shared/components/errors/NotFoundPage';
import { adminRouteElements } from './features/admin/workspace/AdminRoutes';

// Lazy loaded components (non-admin)
const AdminWorkspacePage = lazy(() => import('./features/admin/workspace/AdminWorkspacePage'));
const NoticePage = lazy(() => import('@/features/public/notices/NoticePage'));
const NoticesListPage = lazy(() => import('@/features/public/notices/NoticesListPage'));
const NoticesByTypePage = lazy(() => import('@/features/public/notices/NoticesByTypePage'));
// Portal status draft: src/_draft/portal-status/PortalStatusPage.jsx
const HomePage = lazy(() => import('@/features/public/home/HomePage'));
const UserProfilePage = lazy(() => import('@/features/user/profile/UserProfilePage'));

// TODO [HIGH PRIORITY]: Proveriti zaštitu ruta za neulogovane korisnike.
// Implementirano: ProtectedRoute, redirekt neulogovanih na početnu, auto-logout ako /login ne vrati korisnika.
// Pre produkcije: proveriti da sve rute osim javnih zahtevaju autentifikaciju.

// Komponenta za postavljanje title-a na osnovu rute
/** Postavlja `document.title` preko `useRouteTitle` hook-a. */
function AppTitleSetter() {
  useRouteTitle();
  return null;
}

/** Root komponenta — obavija aplikaciju Query, Helmet, PageTitle i Auth providerima. */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <PageTitleProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </PageTitleProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

/** Glavni sadržaj aplikacije: router, header, rute i toast notifikacije. */
function AppContent() {
  const { user, isLoading } = useAuth();
  useJustLoggedInFeedback();
  usePrefetchUserProfileOnIntent();

  if (isLoading && !user) {
    return (
      <LoadingOverlay
        isLoading={true}
        title="Учитавање..."
        message="Молимо сачекајте док се подаци учитавају..."
      />
    );
  }

  return (
    <Router
      future={{
        // false: navigacija odmah (startTransition odlaže rutu dok Home još renderuje / query-je)
        v7_startTransition: false,
        v7_relativeSplatPath: true,
      }}
    >
      <ScrollToTop />
      {GA_TRACKING_ID ? (
        <>
          <GoogleAnalyticsInit />
          <GoogleAnalyticsPageViews />
        </>
      ) : null}
      <RouteTransitionOverlay />
      <AppTitleSetter />
      <AccessibilityPanelWrapper />
      <HomeSectionNavProvider>
        <div className="App">
          <a href="#main-content" className="skip-link">
            Прескочи на главни садржај
          </a>
          <HomeAppHeader />
          <main id="main-content" tabIndex={-1}>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path={APP_PATHS.AUTH.LOGIN} element={<LoginLaunchPage />} />
                <Route path="/signin-oidc" element={<SigninRedirectPage />} />
                <Route path="/signout-callback-oidc" element={<SignoutCallbackPage />} />
                <Route path="/" element={<HomePage />} />
                <Route path="/objave" element={<NoticesListPage />} />
                <Route path="/objave/kategorija/:type" element={<NoticesByTypePage />} />
                <Route path="/objave/:slug" element={<NoticePage />} />
                <Route path="/profil/*" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminWorkspacePage /></ProtectedRoute>}>
                  {adminRouteElements}
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <Toaster
            position="bottom-center"
            containerStyle={{ bottom: '20px', zIndex: 10010 }}
            toastOptions={{
              duration: TOAST_DURATION.EXTENDED,
              style: {
                background: '#243964f3',
                color: '#fff',
                padding: '16px 40px 16px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                maxWidth: '500px',
                minWidth: '300px',
                zIndex: 10010,
                position: 'relative',
              },
              success: { iconTheme: { primary: '#fff', secondary: '#243964f3' } },
              error: {
                style: { background: '#9c0303f3', zIndex: 10010 },
                iconTheme: { primary: '#fff', secondary: '#9c0303f3' },
              },
            }}
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type !== 'loading' && (
                      <button
                        type="button"
                        className="app-toast-close"
                        onClick={() => toast.dismiss(t.id)}
                        aria-label="Затвори обавештење"
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>
        </div>
      </HomeSectionNavProvider>
    </Router>
  );
}

export default App;
